import { NavLink, useLocation } from 'react-router-dom';

// Icons
const HomeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const UsersIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MapIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 7m0 13V7" />
  </svg>
);

const LocationIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const LibraryIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

interface SidebarProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar = ({
  isMobile = false,
  isOpen = true,
  onClose,
}: SidebarProps): JSX.Element => {
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home', icon: <HomeIcon /> },
    { path: '/npc-generator', label: 'NPC Generator', icon: <UsersIcon /> },
    { path: '/mission-generator', label: 'Mission Generator', icon: <MapIcon /> },
    { path: '/location-generator', label: 'Location Generator', icon: <LocationIcon /> },
    { path: '/content-library', label: 'Library', icon: <LibraryIcon /> },
  ];

  const sidebarStyle: React.CSSProperties = {
    position: 'fixed',
    left: isMobile ? (isOpen ? 0 : '-256px') : 0,
    top: 0,
    height: '100vh',
    width: '256px',
    backgroundColor: '#0f0f1e',
    borderRight: '1px solid rgba(212, 175, 55, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 50,
    transition: 'left 0.3s ease',
  };

  return (
    <aside style={sidebarStyle}>
      {/* Logo Section */}
      <div style={{ padding: '24px', borderBottom: '1px solid rgba(212, 175, 55, 0.2)' }}>
        <NavLink
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #d4af37 0%, #b8962e 100%)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
            }}
          >
            <span
              style={{
                color: '#0f0f1e',
                fontFamily: 'Cinzel, serif',
                fontWeight: 700,
                fontSize: '20px',
              }}
            >
              B
            </span>
          </div>
          <div>
            <h1
              style={{
                color: '#d4af37',
                fontFamily: 'Cinzel, serif',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              Blades
            </h1>
            <p style={{ color: 'rgba(212, 175, 55, 0.6)', fontSize: '12px', margin: 0 }}>
              Content Generator
            </p>
          </div>
        </NavLink>
      </div>

      {/* Navigation */}
      <nav
        style={{
          flex: 1,
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          overflowY: 'auto',
        }}
      >
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => isMobile && onClose?.()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                borderRadius: '8px',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                backgroundColor: isActive ? '#d4af37' : 'transparent',
                color: isActive ? '#0f0f1e' : '#9ca3af',
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)';
                  e.currentTarget.style.color = '#d4af37';
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#9ca3af';
                }
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>{item.icon}</span>
              <span style={{ fontWeight: 500, fontSize: '14px' }}>{item.label}</span>
              {isActive && (
                <div
                  style={{
                    marginLeft: 'auto',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: '#0f0f1e',
                  }}
                />
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ padding: '16px', borderTop: '1px solid rgba(212, 175, 55, 0.2)' }}>
        <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
          <p style={{ margin: 0 }}>Blades in the Dark</p>
          <p style={{ margin: '4px 0 0 0' }}>Content Generator v0.1</p>
        </div>
      </div>
    </aside>
  );
};
